"use client";

import Link from "@/app/components/LocalizedLink";
import { useState, useRef, useEffect } from "react";
import { usePathname } from "next/navigation";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { LogoIcon } from "../Icons";
import { AnimatedIcon } from "./AnimatedIcon";
import { ARROW_ICON } from "./animatedIcons";
import { Finn } from "./Finn";
import { useSession } from "next-auth/react";
import { useUserRole } from "@/app/hooks/useUserRole";
import { useNavOverDark } from "@/app/hooks/useNavOverDark";
import LogoutButton from "./LogoutButton";
import { MobileTabBar } from "./MobileTabBar";
import { removeLocaleFromPath } from "@/app/lib/i18n-config";
import { ORBIT_EASE, PRIMARY_PILL_ICON } from "./orbitButtons";

const DEMO_BOOKING_URL = "https://calendly.com/bacaj-mergim";

// Emails that may open the admin App Settings page.
const APP_SETTINGS_EMAILS = ["mohammed@achtfach.ch"];

// Action buttons take the editor's pill shapes: cobalt glass for actions, a
// ghost glass capsule for login, red logout. Nav links, their dropdowns and
// the account pill use the editor's capsule rows.
//
// On a dark bar (`bgIsDark`), and while dark media passes under the bar
// (useNavOverDark: sections marked data-nav-dark or data-theme="dark"), the
// bar carries data-theme="dark", so every glass control in it, menus
// included, turns into dark glass; the helpers below take `dark` to give
// their labels light text to match.
//
// The glass buttons animate their colours only (transition-colors): the
// material lights their edge on press with box-shadows, which must never
// animate.

/** Blue action pill (demo, register); its icon sits in PRIMARY_PILL_ICON. */
const BLUE_PILL = `btn-glass-primary group gap-2 transition-colors duration-300 ${ORBIT_EASE}`;

/** Login pill: a ghost glass capsule. On a dark bar the material makes it
 *  dark glass with a light label, so it needs no outlined variant. */
const LOGIN_PILL = `btn-glass-ghost transition-colors duration-300 ${ORBIT_EASE}`;

/** Red logout pill. */
const RED_PILL = `inline-flex items-center justify-center whitespace-nowrap rounded-full bg-red-600 font-semibold text-white transition-[background-color,transform] duration-300 ${ORBIT_EASE} hover:bg-red-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50`;

/** A nav link as a capsule; the active one is a raised glass pill. */
const navLinkClass = (active: boolean, dark: boolean) =>
  `inline-flex items-center gap-1 whitespace-nowrap rounded-full px-3.5 py-2 transition-colors duration-300 ${ORBIT_EASE} ${
    active
      ? dark
        ? "glass-raised text-white"
        : "glass-raised text-signal-deep"
      : dark
        ? "text-white hover:bg-white/10"
        : "text-signal-ink hover:bg-white/40"
  }`;

/** Strong glass popover under a desktop dropdown toggle: menu text reads
 *  over anything, and nested in the bar the material drops its blur. */
const DROPDOWN_PANEL =
  "glass-strong absolute left-0 top-full z-50 mt-2 animate-orbit-fade rounded-[1.25rem] p-1.5 motion-reduce:animate-none";

const dropdownItemClass = (active: boolean, dark: boolean) =>
  `block rounded-full px-3.5 py-2 text-sm transition-colors duration-300 ${ORBIT_EASE} ${
    active
      ? dark
        ? "glass-raised text-white"
        : "glass-raised text-signal-deep"
      : dark
        ? "text-white hover:bg-white/10"
        : "text-signal-ink hover:bg-white/60"
  }`;

/** Row in the phone menu; nested rows (dropdown children) sit indented. */
const mobileLinkClass = (active: boolean, dark: boolean, nested = false) =>
  `block rounded-full ${nested ? "px-6 text-[14px]" : "px-4 text-[15px]"} py-2.5 font-medium transition-colors duration-300 ${ORBIT_EASE} ${
    active
      ? dark
        ? "glass-raised text-white"
        : "glass-raised text-signal-deep"
      : dark
        ? "text-white hover:bg-white/10"
        : "text-signal-ink hover:bg-white/60"
  }`;

/** Initial of the signed-in account, in a tinted disc. */
const accountInitialClass = (dark: boolean) =>
  `glass-inset flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[13px] font-semibold uppercase ${
    dark ? "text-white" : "text-signal-deep"
  }`;

const Chevron = ({
  open,
  className = "",
}: {
  open: boolean;
  className?: string;
}) => (
  <svg
    className={`h-4 w-4 transition-transform duration-300 ${ORBIT_EASE} ${open ? "rotate-180" : ""} ${className}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.6}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <AnimatedIcon icon={ARROW_ICON} className="h-[15px] w-[15px]" />
);

const ExternalArrowIcon = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M7 17L17 7M9 7h8v8" />
  </svg>
);

export default function HomeNavbar({
  bgIsDark = false,
  transparentBg = false,
  fullWidth = false,
  floating = false,
}: {
  bgIsDark?: boolean;
  transparentBg?: boolean;
  /** Span the app's working width instead of the 1440px content column;
   *  with `floating`, the pill lines up with a 95%-wide app shell. */
  fullWidth?: boolean;
  /** Landing-page treatment: the bar sits inset from the viewport as a
   *  rounded card instead of spanning edge to edge. */
  floating?: boolean;
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);
  const adminDropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { data: session } = useSession();
  const { isAdmin } = useUserRole();
  const pathname = usePathname();
  // While dark media passes under the label row the bar turns into dark
  // glass, the way a dark bar does, instead of keeping navy labels on it.
  const pillRef = useRef<HTMLDivElement>(null);
  const overDark = useNavOverDark(pillRef);
  const dark = bgIsDark || overDark;

  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const aboutDropdownRef = useRef<HTMLDivElement>(null);
  const [isAboutMobileOpen, setIsAboutMobileOpen] = useState(false);
  const [isAdminMobileOpen, setIsAdminMobileOpen] = useState(false);
  const [isNavVisible, setIsNavVisible] = useState(true);
  const lastScrollY = useRef(0);

  // Hide on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;
      if (delta > 5 && currentY > 80) {
        setIsNavVisible(false);
        setIsMenuOpen(false);
      } else if (delta < -5) {
        setIsNavVisible(true);
      }
      lastScrollY.current = currentY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        adminDropdownRef.current &&
        !adminDropdownRef.current.contains(event.target as Node)
      ) {
        setIsAdminDropdownOpen(false);
      }
      if (
        aboutDropdownRef.current &&
        !aboutDropdownRef.current.contains(event.target as Node)
      ) {
        setIsAboutDropdownOpen(false);
      }
    }

    if (isAdminDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    if (isAboutDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isAdminDropdownOpen, isAboutDropdownOpen]);

  // Close mobile menu when screen size changes to desktop
  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Close menu when clicking on links
  const closeMobileMenu = () => {
    setIsMenuOpen(false);
  };

  // Helper function to check if a path is active
  // Strips locale prefix before comparing
  const isActivePath = (path: string) => {
    const cleanPathname = removeLocaleFromPath(pathname || "/");
    if (path === "/") {
      return cleanPathname === "/";
    }
    return cleanPathname?.startsWith(path);
  };

  const aboutLinks = [
    { href: "/about-us", label: t("navbar.aboutUs") },
    { href: "/blog", label: t("navbar.blog") },
    { href: "/resources", label: t("navbar.resources") || "Ressourcen" },
    {
      href: "/resources/links",
      label: t("navbar.resourcesLinks") || "Behörden & Links",
    },
    {
      href: "/resources/downloads",
      label: t("navbar.resourcesDownloads") || "Downloads",
    },
  ];

  const adminLinks = [
    { href: "/admin/callsheet", label: "Callsheet" },
    { href: "/admin/applicants", label: "Applicants" },
    { href: "/admin/emails", label: "Emails" },
    { href: "/admin/landing-pages", label: "Landing Pages" },
    { href: "/reports", label: "Reports" },
    { href: "/admin/b2b-reports", label: "B2B Reports" },
    { href: "/admin/contacts", label: "Contacts" },
    { href: "/admin/users", label: "Users" },
    { href: "/admin/bexio", label: "Bexio Billing" },
    ...(session?.user?.email && APP_SETTINGS_EMAILS.includes(session.user.email)
      ? [{ href: "/admin/settings", label: "App Settings" }]
      : []),
  ];

  const isAboutActive =
    isActivePath("/about-us") ||
    isActivePath("/blog") ||
    isActivePath("/resources");
  const isAdminActive = isActivePath("/admin") || isActivePath("/reports");
  const accountName = session?.user?.name || session?.user?.email || "";

  const logoLinkTo = session ? "/dashboard" : "/";
  const navSurfaceStyle =
    transparentBg || floating
      ? {
          backgroundColor: "transparent",
        }
      : bgIsDark
        ? { backgroundColor: "#111111" }
        : undefined;
  // Light full-width bars are a glass strip over the page wallpaper, marked
  // as a bar so the material gives them the scroll-edge fill.
  const isGlassBar = !transparentBg && !floating && !bgIsDark;

  return (
    <>
      <div
        aria-hidden="true"
        className={`h-16 ${transparentBg && !floating ? "hidden" : ""}`}
      />
      <nav
        data-lg-bar={isGlassBar ? "" : undefined}
        // The full-width glass bar is its own surface, so over dark media it
        // turns dark itself; the pill below carries the floating bar's theme.
        data-theme={isGlassBar && overDark ? "dark" : undefined}
        className={`fixed top-0 left-0 right-0 w-full z-20 transition-transform duration-300 ${
          floating ? "pt-3 sm:pt-4" : ""
        } ${isGlassBar ? "glass rounded-none" : "bg-transparent"}`}
        style={{
          transform: isNavVisible ? "translateY(0)" : "translateY(-100%)",
          ...navSurfaceStyle,
        }}
      >
        {/* The floating pill is a lens, marked as a bar in markup so it
            paints the bar recipe (clear rim band, frosted label row) from the
            first frame instead of switching once the runtime tags it. A dark
            bar, or any bar over dark media, turns everything in it dark
            glass. useNavOverDark watches this row. */}
        <div
          ref={pillRef}
          data-lg-bar={floating ? "" : undefined}
          data-theme={dark ? "dark" : undefined}
          className={`w-full ${
            floating
              ? `${fullWidth ? "max-w-[calc(100%-22px)] sm:max-w-[95%]" : "max-w-[1360px]"} glass-pill glass-lens px-4 sm:px-6`
              : `${fullWidth ? "max-w-[95%]" : "max-w-[1440px]"} px-4 sm:px-6 lg:px-8`
          } mx-auto h-auto`}
          style={{
            transform: isNavVisible ? "translateY(0)" : "translateY(-100%)",
          }}
        >
          <div className="flex justify-between items-center h-16 sm:h-16">
            {/* Logo */}
            <div className="flex-shrink-0">
              <Link
                href={logoLinkTo}
                className="flex lg:hidden w-[47px] h-[47px] items-center justify-center"
                aria-label="heyFinn"
              >
                <Finn mark className="h-[38px] w-[33px]" />
              </Link>
              <Link
                href={logoLinkTo}
                className="hidden lg:block w-[173px] h-[42px]"
                aria-label="heyFinn"
              >
                <LogoIcon animate color={!dark ? "#0A0B10" : "#FFFFFF"} />
              </Link>
            </div>

            {/* Desktop Navigation: capsule links, then the demo capsule */}
            <div className="hidden lg:flex flex-1 justify-center items-center min-h-[47px]">
              <div className="flex items-center gap-0.5 xl:gap-1.5 text-sm xl:text-[15px] font-medium">
                {!session?.user && (
                  <Link
                    href="/"
                    className={navLinkClass(
                      isActivePath("/") || isActivePath("/home"),
                      dark
                    )}
                  >
                    {t("navbar.home")}
                  </Link>
                )}
                {/* The dashboard belongs to signed-in users; logged-out
                    visitors get pricing in its place. */}
                {session?.user && (
                  <Link
                    href="/dashboard"
                    className={navLinkClass(isActivePath("/dashboard"), dark)}
                  >
                    {t("navbar.dashboard")}
                  </Link>
                )}
                <Link
                  href="/open-positions"
                  className={navLinkClass(
                    isActivePath("/open-positions"),
                    dark
                  )}
                >
                  {t("navbar.openPositions")}
                </Link>
                {!session?.user && (
                  <Link
                    href="/home/pricing"
                    className={navLinkClass(
                      isActivePath("/home/pricing"),
                      dark
                    )}
                  >
                    {t("navbar.pricing")}
                  </Link>
                )}
                <div className="relative" ref={aboutDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsAboutDropdownOpen(!isAboutDropdownOpen)}
                    aria-expanded={isAboutDropdownOpen}
                    className={navLinkClass(isAboutActive, dark)}
                  >
                    {t("navbar.aboutUs")}
                    <Chevron open={isAboutDropdownOpen} />
                  </button>
                  {isAboutDropdownOpen && (
                    <div className={`${DROPDOWN_PANEL} w-56`}>
                      {aboutLinks.map(link => (
                        <Link
                          key={link.href}
                          href={link.href}
                          onClick={() => setIsAboutDropdownOpen(false)}
                          className={dropdownItemClass(
                            isActivePath(link.href),
                            dark
                          )}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <div className="relative" ref={adminDropdownRef}>
                    <button
                      type="button"
                      onClick={() =>
                        setIsAdminDropdownOpen(!isAdminDropdownOpen)
                      }
                      aria-expanded={isAdminDropdownOpen}
                      className={navLinkClass(isAdminActive, dark)}
                    >
                      Admin
                      <Chevron open={isAdminDropdownOpen} />
                    </button>
                    {isAdminDropdownOpen && (
                      <div className={`${DROPDOWN_PANEL} w-48`}>
                        {adminLinks.map(link => (
                          <Link
                            key={link.href}
                            href={link.href}
                            onClick={() => setIsAdminDropdownOpen(false)}
                            className={dropdownItemClass(
                              isActivePath(link.href),
                              dark
                            )}
                          >
                            {link.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <a
                  href={DEMO_BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${BLUE_PILL} ml-2 h-11 whitespace-nowrap pl-4 pr-2 text-[13px] xl:pl-5 xl:text-[14px]`}
                >
                  <span>{t("navbar.bookDemo")}</span>
                  <span aria-hidden="true" className={PRIMARY_PILL_ICON}>
                    <ExternalArrowIcon />
                  </span>
                </a>
              </div>
            </div>

            {/* Right side: account capsules + language switcher */}
            <div className="hidden lg:flex items-center gap-2 xl:gap-3">
              {session?.user ? (
                <>
                  <Link
                    href="/profile"
                    className={`btn-glass-ghost max-w-[160px] gap-2 py-1 pl-1 pr-3.5 transition-colors duration-300 ${ORBIT_EASE} xl:max-w-[220px]`}
                  >
                    <span
                      aria-hidden="true"
                      className={accountInitialClass(dark)}
                    >
                      {accountName.charAt(0)}
                    </span>
                    <span
                      className={`truncate text-[13px] font-semibold xl:text-[14px] ${
                        dark ? "text-white" : "text-signal-ink"
                      }`}
                    >
                      {accountName}
                    </span>
                  </Link>
                  <LogoutButton
                    bare
                    className={`${RED_PILL} h-11 px-4 text-[13px] xl:text-[14px]`}
                  >
                    {t("navbar.logout") || "Logout"}
                  </LogoutButton>
                </>
              ) : (
                <>
                  <Link
                    href="/login"
                    className={`${LOGIN_PILL} h-11 whitespace-nowrap px-5 text-[13px] xl:text-[14px]`}
                  >
                    {t("navbar.login")}
                  </Link>
                  <Link
                    href="/register"
                    className={`${BLUE_PILL} h-11 whitespace-nowrap pl-5 pr-2 text-[13px] xl:text-[14px]`}
                  >
                    <span>{t("navbar.register")}</span>
                    <span aria-hidden="true" className={PRIMARY_PILL_ICON}>
                      <ArrowRightIcon />
                    </span>
                  </Link>
                </>
              )}

              <LanguageSwitcher variant="pill" />
            </div>

            {/* Phone: the free-trial capsule and a menu disc */}
            <div className="lg:hidden flex items-center gap-2">
              {!session &&
                !(
                  pathname.includes("login") || pathname.includes("register")
                ) && (
                  <Link
                    href="/register"
                    className="group navbar-mobile-cta rounded-full"
                  >
                    {/* The label keeps the flowing gradient border around it */}
                    <span className="navbar-mobile-cta__label gap-2 rounded-full pl-4 pr-1.5">
                      <span>{t("navbar.tryForFree")}</span>
                      <span aria-hidden="true" className={PRIMARY_PILL_ICON}>
                        <ArrowRightIcon />
                      </span>
                    </span>
                  </Link>
                )}
              <button
                type="button"
                onClick={handleMenuToggle}
                className={`btn-glass-ghost relative h-10 w-10 transition-colors duration-300 ${ORBIT_EASE} ${
                  dark ? "text-white" : "text-signal-ink hover:text-signal-deep"
                }`}
                aria-expanded={isMenuOpen}
                aria-label="Toggle main menu"
              >
                {/* Three lines that fold into an X: the outer two rotate onto
                    the middle one, which shrinks away. */}
                <span aria-hidden="true" className="relative block h-3 w-4">
                  <span
                    data-testid="menu-line-top"
                    className={`absolute left-0 top-0 h-[1.5px] w-4 rounded-full bg-current transition-transform duration-500 ${ORBIT_EASE} ${
                      isMenuOpen ? "translate-y-[5.25px] rotate-45" : ""
                    }`}
                  />
                  <span
                    data-testid="menu-line-middle"
                    className={`absolute left-0 top-[5.25px] h-[1.5px] w-4 rounded-full bg-current transition-[opacity,transform] duration-300 ${ORBIT_EASE} ${
                      isMenuOpen ? "scale-x-0 opacity-0" : ""
                    }`}
                  />
                  <span
                    data-testid="menu-line-bottom"
                    className={`absolute bottom-0 left-0 h-[1.5px] w-4 rounded-full bg-current transition-transform duration-500 ${ORBIT_EASE} ${
                      isMenuOpen ? "-translate-y-[5.25px] -rotate-45" : ""
                    }`}
                  />
                </span>
              </button>
            </div>
          </div>

          {/* Mobile Navigation: a strong glass popover of capsule rows */}
          {isMenuOpen && (
            <div className="lg:hidden relative">
              <div
                className={`absolute right-0 mt-1 w-72 max-w-[calc(100vw-2.5rem)] animate-orbit-fade glass-strong rounded-[20px] p-2 motion-reduce:animate-none ${
                  dark ? "text-white" : "text-signal-ink"
                }`}
              >
                {/* Navigation Links */}
                <div className="space-y-0.5">
                  {!session?.user && (
                    <Link
                      href="/"
                      onClick={closeMobileMenu}
                      className={mobileLinkClass(
                        isActivePath("/") || isActivePath("/home"),
                        dark
                      )}
                    >
                      {t("navbar.home")}
                    </Link>
                  )}
                  {session?.user && (
                    <Link
                      href="/dashboard"
                      onClick={closeMobileMenu}
                      className={mobileLinkClass(
                        isActivePath("/dashboard"),
                        dark
                      )}
                    >
                      {t("navbar.dashboard")}
                    </Link>
                  )}
                  <Link
                    href="/open-positions"
                    onClick={closeMobileMenu}
                    className={mobileLinkClass(
                      isActivePath("/open-positions"),
                      dark
                    )}
                  >
                    {t("navbar.openPositions")}
                  </Link>
                  {!session?.user && (
                    <Link
                      href="/home/pricing"
                      onClick={closeMobileMenu}
                      className={mobileLinkClass(
                        isActivePath("/home/pricing"),
                        dark
                      )}
                    >
                      {t("navbar.pricing")}
                    </Link>
                  )}
                  <div className="space-y-0.5">
                    <button
                      type="button"
                      onClick={() => setIsAboutMobileOpen(!isAboutMobileOpen)}
                      className={`${mobileLinkClass(false, dark)} flex w-full items-center justify-between text-left`}
                      aria-expanded={isAboutMobileOpen}
                    >
                      <span>{t("navbar.aboutUs")}</span>
                      <Chevron open={isAboutMobileOpen} className="h-5 w-5" />
                    </button>
                    {isAboutMobileOpen &&
                      aboutLinks.map(link => (
                        <Link
                          key={link.href}
                          href={link.href}
                          onClick={closeMobileMenu}
                          className={mobileLinkClass(
                            isActivePath(link.href),
                            dark,
                            true
                          )}
                        >
                          {link.label}
                        </Link>
                      ))}
                  </div>
                  {isAdmin && (
                    <div className="space-y-0.5">
                      <button
                        type="button"
                        onClick={() => setIsAdminMobileOpen(!isAdminMobileOpen)}
                        className={`${mobileLinkClass(false, dark)} flex w-full items-center justify-between text-left`}
                        aria-expanded={isAdminMobileOpen}
                      >
                        <span className="font-semibold">Admin</span>
                        <Chevron open={isAdminMobileOpen} className="h-5 w-5" />
                      </button>
                      {isAdminMobileOpen &&
                        adminLinks.map(link => (
                          <Link
                            key={link.href}
                            href={link.href}
                            onClick={closeMobileMenu}
                            className={mobileLinkClass(
                              isActivePath(link.href),
                              dark,
                              true
                            )}
                          >
                            {link.label}
                          </Link>
                        ))}
                    </div>
                  )}
                  <a
                    href={DEMO_BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${BLUE_PILL} !mt-3 h-11 w-full pl-5 pr-2 text-[14px]`}
                  >
                    <span>{t("navbar.bookDemo")}</span>
                    <span aria-hidden="true" className={PRIMARY_PILL_ICON}>
                      <ExternalArrowIcon />
                    </span>
                  </a>
                </div>

                {/* Auth Section */}
                <div
                  className={`mt-3 border-t pt-3 ${
                    dark ? "border-white/10" : "border-signal-ink/10"
                  }`}
                >
                  {session?.user ? (
                    <div className="space-y-2">
                      <Link
                        href="/profile"
                        className={`flex items-center gap-2.5 rounded-full py-1.5 pl-1.5 pr-4 transition-colors duration-300 ${ORBIT_EASE} ${
                          dark ? "hover:bg-white/10" : "hover:bg-white/60"
                        }`}
                      >
                        <span
                          aria-hidden="true"
                          className={accountInitialClass(dark)}
                        >
                          {accountName.charAt(0)}
                        </span>
                        <span className="min-w-0 break-words text-sm font-semibold">
                          {accountName}
                        </span>
                      </Link>
                      <LogoutButton
                        bare
                        className={`${RED_PILL} h-11 w-full text-[14px]`}
                      >
                        {t("navbar.logout") || "Logout"}
                      </LogoutButton>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Link
                        href="/login"
                        className={`${LOGIN_PILL} h-11 w-full text-[14px]`}
                      >
                        {t("navbar.login")}
                      </Link>
                      <Link
                        href="/register"
                        className={`${BLUE_PILL} h-11 w-full pl-5 pr-2 text-[14px]`}
                      >
                        <span>{t("navbar.register")}</span>
                        <span aria-hidden="true" className={PRIMARY_PILL_ICON}>
                          <ArrowRightIcon />
                        </span>
                      </Link>
                    </div>
                  )}

                  {/* Language Switcher */}
                  <div
                    className={`mt-3 flex justify-end border-t px-1 pt-3 ${
                      dark ? "border-white/10" : "border-signal-ink/10"
                    }`}
                  >
                    <LanguageSwitcher variant="pill" dropUp />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>
      {/* Outside the nav: its transform would pin the bar to the nav */}
      {session?.user && <MobileTabBar />}
    </>
  );
}
